import React, { memo, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { RendererContext } from '../../contexts/RendererContext';
import type { RenderViewState } from '../../potree/viewer/RenderViewState';
import { RenderViewContext } from '../../contexts/RenderViewContext';
import { HeadlessOrbitController } from '../../potree/navigation/HeadlessOrbitController';
import { WidgetOrbitController } from '../../potree/navigation/WidgetOrbitController';
import { useDragResize } from './useDragResize';
import { ViewSelector } from '../ViewSelector';
import { EarthLoader } from '../loaders/EarthLoader';
import { getWidgetVisibleProperty, getWidgetLayoutXProperty, getWidgetLayoutYProperty, getWidgetLayoutWidthProperty, getWidgetLayoutHeightProperty } from '../../modules/Properties/integration/WidgetSettingsIntegration';
import type { ReactiveProperty } from '@pointorama/potree-shared';
import type { PropertyValueData } from '../../modules/Properties/types';

/**
 * Contents of the floating render-view widget — canvas, controls, handles.
 *
 * The outer animated container is owned by `MorphShell`; this component is
 * mounted only while the shell is in the `open` phase. All previous canvas
 * + orbit + pipeline lifecycle remains intact; the persistent widget
 * RenderView lives on `viewer.ensureWidgetView()` regardless of mount state.
 */
export const RenderViewWidgetContents = memo(function RenderViewWidgetContents() {
  const { viewer } = useContext(RendererContext);
  const parentRef = useRef<HTMLElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const orbitRef = useRef<WidgetOrbitController | null>(null);
  const renderView = viewer ? viewer.ensureWidgetView() : null;

  const layoutX = useReactiveValue<number>(getWidgetLayoutXProperty);
  const layoutY = useReactiveValue<number>(getWidgetLayoutYProperty);
  const layoutW = useReactiveValue<number>(getWidgetLayoutWidthProperty);
  const layoutH = useReactiveValue<number>(getWidgetLayoutHeightProperty);

  useLayoutEffect(() => {
    parentRef.current = document.getElementById('potree_render_area');
  }, []);

  const { dragHandleProps, resizeHandleProps } = useDragResize({
    parentRef: parentRef as React.RefObject<HTMLElement>,
    initial: { x: layoutX, y: layoutY, width: layoutW, height: layoutH },
    // Reserve the 52px tools bar so the widget can't slide underneath it.
    topInset: 52,
    onChange: (next) => {
      getWidgetLayoutXProperty().value = next.x;
      getWidgetLayoutYProperty().value = next.y;
      getWidgetLayoutWidthProperty().value = next.width;
      getWidgetLayoutHeightProperty().value = next.height;
    },
  });

  // Defer mounting the RenderView until at least one pointcloud is loaded.
  // On a fresh page load the widget can mount (because `widget.visible` is
  // restored from cloud state) before the cloud finishes loading; if we
  // built the RenderView right then, the camera would be seeded from a
  // bare scene (default near/far, no fitToScreen) and the widget would
  // render an empty frame until the user toggled it off and back on.
  const [pointcloudsReady, setPointcloudsReady] = useState(() => (viewer?.scene.pointclouds.length ?? 0) > 0);
  useEffect(() => {
    if (!viewer) return;
    if (pointcloudsReady) return;
    const onAdded = () => setPointcloudsReady(true);
    viewer.scene.addEventListener('pointcloud_added', onAdded);
    return () => {
      viewer.scene.removeEventListener('pointcloud_added', onAdded);
    };
  }, [viewer, pointcloudsReady]);

  // Attach the live canvas + camera + orbit to the persistent widget
  // RenderView. The RenderView and its `RenderViewState` live on the viewer
  // for the project's lifetime (`viewer.ensureWidgetView`), independent of
  // this component's mount cycle. That way, tools that push state onto the
  // widget's state (e.g. DoorWindowTool's wall focus) keep applying even
  // while this component is unmounted; reopening the widget re-attaches
  // canvas + camera + orbit and the persisted state drives the first frame.
  useEffect(() => {
    if (!viewer) return;
    if (!pointcloudsReady) return;
    const canvas = canvasRef.current;
    if (!canvas) return;

    const view = viewer.ensureWidgetView();
    const viewState = view.viewState;
    if (!viewState) return;

    const mainCamera = viewer.scene.getActiveCamera();
    const widgetCamera = new THREE.PerspectiveCamera(60, 1, mainCamera.near, mainCamera.far);
    widgetCamera.up.set(0, 0, 1);

    const bbox = viewer.getBoundingBox(viewer.scene.pointclouds);
    const fitTarget = bbox.getCenter(new THREE.Vector3());
    const sphere = bbox.getBoundingSphere(new THREE.Sphere());
    const fovRad = (widgetCamera.fov * Math.PI) / 180;
    const fitRadius = sphere.radius / Math.sin(fovRad / 2);

    const orbit = new WidgetOrbitController(canvas, widgetCamera);
    orbit.view.position.copy(fitTarget).addScaledVector(orbit.view.direction, -fitRadius);
    orbit.view.radius = fitRadius;
    orbit.applyViewToCamera();
    orbitRef.current = orbit;

    const previousCanvas = view.canvas;
    const previousCamera = view.camera;
    const headlessOrbit = new HeadlessOrbitController();

    orbit.onDoubleClick = (mouseX: number, mouseY: number) => {
      const pcs = viewer.scene.pointclouds;
      if (pcs.length === 0) return;

      const currentCamera = view.camera;

      const w = canvas.clientWidth || 1;
      const h = canvas.clientHeight || 1;
      const ndcX = (mouseX / w) * 2 - 1;
      const ndcY = -(mouseY / h) * 2 + 1;

      const raycaster = new THREE.Raycaster();
      raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), currentCamera);

      const pickViewer = viewer as unknown as Parameters<typeof pcs[number]['pick']>[0];

      let bestHit: { position: THREE.Vector3 } | null = null;
      let bestDistance = Infinity;
      for (const pc of pcs) {
        const hit = pc.pick(pickViewer, currentCamera, raycaster.ray, {
          pickClipped: false,
          width: w,
          height: h,
          x: mouseX,
          y: h - mouseY,
        }) as { position?: THREE.Vector3 } | null;
        if (hit?.position) {
          const d = currentCamera.position.distanceTo(hit.position);
          if (d < bestDistance) {
            bestDistance = d;
            bestHit = hit as { position: THREE.Vector3 };
          }
        }
      }
      if (bestHit) orbit.zoomToLocation(bestHit.position);
    };

    viewState.attachOrbit(orbit, widgetCamera, canvas);

    const initialPipeline = viewer.pickWidgetPipeline(view);
    if (view.pipeline !== initialPipeline) {
      view.pipeline.disposeState(view.pipelineState);
      view.pipeline = initialPipeline;
      view.pipelineState = initialPipeline.createState();
    }

    let raf = 0;
    const tick = () => {
      orbit.update();
      const desired = viewer.pickWidgetPipeline(view);
      if (view.pipeline !== desired) {
        view.pipeline.disposeState(view.pipelineState);
        view.pipeline = desired;
        view.pipelineState = desired.createState();
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(raf);
      viewState.detachOrbit(headlessOrbit, previousCanvas, previousCamera);
      orbit.dispose();
      orbitRef.current = null;
    };
  }, [viewer, pointcloudsReady]);

  const onClose = () => {
    getWidgetVisibleProperty().value = false;
  };

  return (
    <>
      {/* The canvas fills the widget. Mouse drag/wheel are routed to the
          orbit controller (no React handlers — it binds to the canvas). */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full block focus:outline-none"
        tabIndex={0}
      />

      {!pointcloudsReady && <EarthLoader id={undefined} />}

      {/* Drag handle: a thin invisible strip across the top edge. */}
      <div
        {...dragHandleProps}
        className="absolute top-0 left-0 right-12 h-3 cursor-move"
        title="Drag to move"
      />

      {/* Minimize button — top right corner. */}
      <button
        type="button"
        onClick={onClose}
        aria-label="Hide secondary view"
        title="Hide secondary view"
        className="absolute z-20 top-1 right-1 w-6 h-6 flex items-center justify-center cursor-pointer text-gray-800/90 dark:text-white/90 transition-opacity opacity-[.55] hover:opacity-100"
      >
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
          <path d="M6.5 1.5 L3 5 L6.5 8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {renderView && (
        <RenderViewContext.Provider value={renderView}>
          <div className="absolute bottom-2 left-2">
            <ViewSelector />
          </div>
        </RenderViewContext.Provider>
      )}

      <div className="absolute bottom-2 right-2 flex flex-col rounded-md overflow-hidden bg-white/90 dark:bg-black/60 ring-1 ring-black/20 dark:ring-white/15">
        <button
          type="button"
          onClick={() => orbitRef.current?.zoom(1)}
          aria-label="Zoom in"
          title="Zoom in"
          className="w-7 h-7 flex items-center justify-center text-gray-800 dark:text-gray-100 hover:bg-neon-green-300 hover:text-black"
        >
          +
        </button>
        <div className="h-px bg-black/15 dark:bg-white/15" />
        <button
          type="button"
          onClick={() => orbitRef.current?.zoom(-1)}
          aria-label="Zoom out"
          title="Zoom out"
          className="w-7 h-7 flex items-center justify-center text-gray-800 dark:text-gray-100 hover:bg-neon-green-300 hover:text-black"
        >
          −
        </button>
      </div>

      <div
        {...resizeHandleProps}
        className="absolute right-0 bottom-0 w-3 h-3 cursor-nwse-resize"
        aria-label="Resize widget"
      />
    </>
  );
});

// ---- shared helpers (exported for MorphShell) ----------------------------

/** Mirror of ProjectContent's backgroundColors map. */
export const BACKGROUND_HEX: Record<string, string> = {
  black: '#121212',
  white: '#ffffff',
  dark: '#313131',
  light: '#cdcdcd',
};

/**
 * Subscribe to a RenderViewState's `densityModeOverride` reactive property,
 * returning its current value (`true` while the view renders in X-Ray).
 */
export function useDensityModeOverride(state: RenderViewState | null): boolean | undefined {
  const [value, setValue] = useState<boolean | undefined>(() => state?.densityModeOverride.value);
  const [lastState, setLastState] = useState(state);
  if (lastState !== state) {
    setLastState(state);
    setValue(state?.densityModeOverride.value);
  }
  useEffect(() => {
    if (!state) return;
    const sub = state.densityModeOverride.subscribe((v) => setValue(v));
    return () => sub.unsubscribe();
  }, [state]);
  return value;
}

/** Minimal view of the viewer's background interface the widget consumes. */
interface BackgroundViewer {
  getBackground?(): string | null;
}

/**
 * Derive the widget's theme background ('dark' | 'light') from the SAME
 * source the main view uses — `viewer.background`.
 */
export function readViewerThemeBackground(viewer: BackgroundViewer | null | undefined): 'dark' | 'light' {
  const bg = viewer?.getBackground?.();
  if (bg === 'dark' || bg === 'light') return bg;
  return readThemePreference();
}

function readThemePreference(): 'dark' | 'light' {
  if (typeof localStorage !== 'undefined' && localStorage.theme === 'dark') return 'dark';
  if (typeof localStorage !== 'undefined' && localStorage.theme === 'light') return 'light';
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
}

/**
 * Subscribe to a ReactiveProperty produced by a getter, returning its current
 * value. Re-renders the component whenever the property's value changes.
 */
export function useReactiveValue<T>(getter: () => ReactiveProperty<PropertyValueData>): T {
  const property = getter();
  const [value, setValue] = useState<PropertyValueData>(property.value);
  useEffect(() => {
    const sub = property.subscribe((v) => setValue(v));
    return () => sub.unsubscribe();
  }, [property]);
  return value as T;
}
